import { Loader2, Plus, X, Calendar as CalendarIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Calendar } from '../ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { format } from 'date-fns';
import ImageUploader from './ImageUploader';
import DiscountsSection from './DiscountsSection';
import MultiDaySection from './MultiDaySection';
import { useAuth } from '../../context/AuthContext';

/**
 * Dialog that handles both creation and editing of events.
 * All state lives in the parent AdminDashboard — this component is purely
 * presentational and receives form data plus a set of handlers.
 */
const EventFormDialog = ({
  open,
  onOpenChange,
  editingEvent,
  formData,
  setFormData,
  saving,
  onSubmit,
  onCancel,
  onAddTier,
  onRemoveTier,
  onTierChange,
}) => {
  const { token } = useAuth();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-2xl">
            {editingEvent ? 'EDIT EVENT' : 'CREATE EVENT'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6 mt-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">TITLE</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="event-title-input"
              />
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">DATE</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 flex items-center justify-between focus:border-[#B5FF00] focus:outline-none"
                    data-testid="event-date-input"
                  >
                    {formData.date ? format(new Date(formData.date), 'PPP') : 'Select date'}
                    <CalendarIcon size={18} className="text-zinc-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-zinc-800" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date ? new Date(formData.date) : undefined}
                    onSelect={(date) =>
                      setFormData({ ...formData, date: date ? format(date, 'yyyy-MM-dd') : '' })
                    }
                    className="bg-[#0A0A0A]"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">TIME</label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="event-time-input"
              />
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">VENUE</label>
              <input
                type="text"
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="event-venue-input"
              />
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">GENRE</label>
              <input
                type="text"
                value={formData.genre}
                onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="e.g. Techno, House"
                required
                data-testid="event-genre-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">ADDRESS</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="event-address-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">EVENT IMAGE</label>
              <ImageUploader
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                token={token}
                testIdPrefix="event-image"
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">DESCRIPTION</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full h-24 bg-[#050505] border border-zinc-800 text-white px-4 py-3 focus:border-[#B5FF00] focus:outline-none resize-none"
                required
                data-testid="event-description-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">LINEUP (comma separated)</label>
              <input
                type="text"
                value={formData.lineup}
                onChange={(e) => setFormData({ ...formData, lineup: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="Artist 1, Artist 2, Artist 3"
                data-testid="event-lineup-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-5 h-5 bg-[#050505] border-zinc-800 text-[#B5FF00] focus:ring-[#B5FF00]"
                  data-testid="event-featured-input"
                />
                <span className="font-mono text-sm text-zinc-400">Featured Event (shown in hero)</span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData.waitlist_only}
                  onChange={(e) => setFormData({ ...formData, waitlist_only: e.target.checked })}
                  className="w-5 h-5 mt-0.5 bg-[#050505] border-zinc-800 text-[#B5FF00] focus:ring-[#B5FF00]"
                  data-testid="event-waitlist-only-input"
                />
                <span>
                  <span className="font-mono text-sm text-zinc-300 block">Waitlist Only</span>
                  <span className="font-mono text-xs text-zinc-500 tracking-wide">
                    Hides ticket tiers on the public page and shows a &ldquo;Join Waitlist&rdquo; form instead.
                    Use for events that aren&apos;t on sale yet.
                  </span>
                </span>
              </label>
            </div>

            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">MIN AGE</label>
              <input
                type="number"
                min="0"
                max="99"
                value={formData.min_age ?? 21}
                onChange={(e) => setFormData({ ...formData, min_age: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="21"
                data-testid="event-min-age-input"
              />
              <p className="text-zinc-500 text-xs mt-1 font-mono">Guests below this age are blocked from RSVPing.</p>
            </div>

            <div className="md:col-span-2">
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">EVENT TERMS &amp; CONDITIONS</label>
              <textarea
                value={formData.terms_conditions || ''}
                onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                className="w-full h-32 bg-[#050505] border border-zinc-800 text-white px-4 py-3 focus:border-[#B5FF00] focus:outline-none resize-none font-mono text-sm"
                placeholder="Plain text. Guests must tick a box agreeing to these before RSVPing. Leave empty to use Unight's default terms."
                data-testid="event-terms-input"
              />
            </div>
          </div>

          {/* Event type selector */}
          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-3 block">EVENT TYPE</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  value: 'ticketed',
                  label: 'TICKETING',
                  sub: 'Sell tickets + offer guest lists',
                  blurb: 'Standard event. Tiers below set the prices. Promoters can still build guest lists.',
                },
                {
                  value: 'guest_list',
                  label: 'GUEST LIST ONLY',
                  sub: 'No tickets — invite-only',
                  blurb: 'Event is not listed for ticket purchase. Entry is exclusively via promoter guest lists.',
                },
              ].map((opt) => {
                const active = (formData.event_type || 'ticketed') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, event_type: opt.value })}
                    className={`text-left p-4 border transition-all ${
                      active ? 'border-[#B5FF00] bg-[#B5FF00]/5' : 'border-zinc-800 bg-[#050505] hover:border-zinc-600'
                    }`}
                    data-testid={`event-type-${opt.value}`}
                  >
                    <div className={`font-['Josefin_Sans'] text-lg font-bold ${active ? 'text-[#B5FF00]' : 'text-white'}`}>
                      {opt.label}
                    </div>
                    <div className="font-mono text-xs text-zinc-500 tracking-wider mb-2">{opt.sub}</div>
                    <p className="text-sm text-zinc-400 leading-relaxed">{opt.blurb}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Ticket Tiers — only for ticketed events */}
          {(formData.event_type || 'ticketed') === 'ticketed' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="font-mono text-xs text-zinc-400 tracking-widest">TICKET TIERS</label>
              <button
                type="button"
                onClick={onAddTier}
                className="text-[#B5FF00] font-mono text-xs flex items-center gap-1 hover:underline"
              >
                <Plus size={14} /> ADD TIER
              </button>
            </div>

            <div className="space-y-4">
              {formData.ticket_tiers.map((tier, index) => (
                <div key={tier.id || `tier-${index}`} className="bg-[#050505] border border-zinc-800 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-mono text-xs text-zinc-500">TIER {index + 1}</span>
                    {formData.ticket_tiers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onRemoveTier(index)}
                        className="text-zinc-500 hover:text-red-500"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      value={tier.name}
                      onChange={(e) => onTierChange(index, 'name', e.target.value)}
                      className="col-span-2 h-10 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                      placeholder="Tier Name (e.g. Phase 1 - Female Single)"
                      required
                      data-testid={`tier-name-${index}`}
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={tier.price}
                      onChange={(e) => onTierChange(index, 'price', e.target.value)}
                      className="h-10 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                      placeholder="Price (₹)"
                      required
                      data-testid={`tier-price-${index}`}
                    />
                    <input
                      type="number"
                      value={tier.quantity_available}
                      onChange={(e) => onTierChange(index, 'quantity_available', e.target.value)}
                      className="h-10 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                      placeholder="Qty"
                      required
                      data-testid={`tier-qty-${index}`}
                    />
                  </div>
                  {/* Optional single-line note rendered under the name on the ticket card
                      (e.g. "Includes ₹1000 cover charge"). Kept separate from `name` so
                      the pricing line stays clean and the note stays muted. */}
                  <input
                    type="text"
                    value={tier.description || ''}
                    onChange={(e) => onTierChange(index, 'description', e.target.value)}
                    className="mt-3 w-full h-10 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                    placeholder="Optional note — e.g. Includes ₹1000 cover charge"
                    data-testid={`tier-description-${index}`}
                  />
                  {/* Optional privacy toggle. When on, buyers won't see the
                      "N remaining" indicator on the event page (still shows
                      SOLD OUT once the tier hits 0). Handy when you don't
                      want to broadcast slow sales or, alternatively, when
                      you want to create a sense of scarcity without a
                      specific count. */}
                  <label
                    className="mt-3 flex items-center gap-2 cursor-pointer select-none"
                    data-testid={`tier-hide-remaining-label-${index}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!tier.hide_remaining}
                      onChange={(e) => onTierChange(index, 'hide_remaining', e.target.checked)}
                      className="accent-[#B5FF00] w-4 h-4"
                      data-testid={`tier-hide-remaining-${index}`}
                    />
                    <span className="font-mono text-[11px] text-zinc-400 tracking-wide">
                      Hide &quot;N remaining&quot; from buyers (still shows SOLD OUT at 0)
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Organiser payout email + sales kill-switch */}
          {formData.event_type !== 'guest_list' && (
            <div className="border-t border-zinc-800 pt-6 space-y-4">
              <div>
                <label className="block text-white font-mono text-sm mb-2 tracking-widest">
                  ORGANISER EMAIL (OPTIONAL)
                </label>
                <input
                  type="email"
                  value={formData.organizer_email || ''}
                  onChange={(e) => setFormData({ ...formData, organizer_email: e.target.value })}
                  placeholder="ops@venue.com"
                  className="w-full h-11 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="event-organizer-email-input"
                />
                <p className="text-zinc-500 text-xs mt-2">
                  When the event date passes, we&apos;ll auto-email this address a payout-safe CSV (buyer info + face value only — no Razorpay IDs or platform fee).
                </p>
              </div>

              {/* Fee overrides — leave blank to use defaults; 0 to waive */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-mono text-sm mb-2 tracking-widest">
                    PLATFORM FEE / TICKET (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.platform_fee_per_ticket ?? ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      platform_fee_per_ticket: e.target.value === '' ? null : Number(e.target.value),
                    })}
                    placeholder="Auto (banded ₹50–₹175)"
                    className="w-full h-11 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                    data-testid="event-platform-fee-input"
                  />
                  <p className="text-zinc-500 text-xs mt-2">Blank = auto-band. Set 0 to waive.</p>
                </div>
                <div>
                  <label className="block text-white font-mono text-sm mb-2 tracking-widest">
                    CONVENIENCE FEE / TICKET (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.convenience_fee_per_ticket ?? ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      convenience_fee_per_ticket: e.target.value === '' ? null : Number(e.target.value),
                    })}
                    placeholder="0 (no fee)"
                    className="w-full h-11 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                    data-testid="event-convenience-fee-input"
                  />
                  <p className="text-zinc-500 text-xs mt-2">18% GST auto-applied on both fees.</p>
                </div>
              </div>

              <div>
                <label className="block text-white font-mono text-sm mb-2 tracking-widest">
                  TICKET SALES CLOSE AT (HH:MM)
                </label>
                <input
                  type="time"
                  value={formData.sales_close_time || ''}
                  onChange={(e) => setFormData({ ...formData, sales_close_time: e.target.value || null })}
                  className="w-full h-11 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="event-sales-close-time-input"
                />
                <p className="text-zinc-500 text-xs mt-2">On event date, IST. Blank = open until event starts.</p>
              </div>

              <label
                className="flex items-center gap-3 cursor-pointer select-none"
                data-testid="event-sales-closed-label"
              >
                <input
                  type="checkbox"
                  checked={!!formData.sales_closed}
                  onChange={(e) => setFormData({ ...formData, sales_closed: e.target.checked })}
                  className="accent-[#B5FF00] w-4 h-4"
                  data-testid="event-sales-closed-checkbox"
                />
                <span className="font-mono text-sm text-zinc-300 tracking-wide">
                  CLOSE TICKET SALES · New checkouts will be blocked (existing tickets remain valid)
                </span>
              </label>
            </div>
          )}

          {/* Guest-list controls — visible for both event types since ticketed
              events can still have a promoter guest list attached, and pure
              guest-list events obviously need the kill-switch too. */}
          <div className="border-t border-zinc-800 pt-6 space-y-4">
            <div>
              <label className="block text-white font-mono text-sm mb-2 tracking-widest">
                GUEST LIST CLOSES AT (HH:MM)
              </label>
              <input
                type="time"
                value={formData.guestlist_close_time || ''}
                onChange={(e) => setFormData({ ...formData, guestlist_close_time: e.target.value || null })}
                className="w-full h-11 bg-[#0A0A0A] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                data-testid="event-guestlist-close-time-input"
              />
              <p className="text-zinc-500 text-xs mt-2">On event date, IST. Blank = open until event starts.</p>
            </div>
            <label
              className="flex items-center gap-3 cursor-pointer select-none"
              data-testid="event-guest-list-closed-label"
            >
              <input
                type="checkbox"
                checked={!!formData.guest_list_closed}
                onChange={(e) => setFormData({ ...formData, guest_list_closed: e.target.checked })}
                className="accent-[#B5FF00] w-4 h-4"
                data-testid="event-guest-list-closed-checkbox"
              />
              <span className="font-mono text-sm text-zinc-300 tracking-wide">
                CLOSE GUEST LIST · New RSVPs will be blocked (use when at capacity — existing guests stay valid)
              </span>
            </label>
          </div>

          {/* Multi-day events — dates + per-tier date scope */}
          {formData.event_type !== 'guest_list' && (
            <MultiDaySection formData={formData} setFormData={setFormData} />
          )}

          {/* Waitlist Policy — per-event control over which party types auto-waitlist */}
          <div className="border-t border-zinc-800 pt-6 space-y-3" data-testid="waitlist-policy-section">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest">AUTO-WAITLIST THESE GUEST TYPES</label>
              <p className="text-zinc-500 text-xs mt-1">
                Any category you tick will land on the <span className="text-amber-400">waitlist</span> after RSVP — promoter approval required before QR issue. Everything unticked auto-confirms.
              </p>
            </div>
            {(() => {
              const CATS = [
                { key: 'stag_male',   label: 'STAG (SOLO MALE)',    sub: 'party_type=single · gender=male' },
                { key: 'solo_female', label: 'SOLO FEMALE',          sub: 'party_type=single · gender=female' },
                { key: 'couple_mf',   label: 'COUPLE (M+F)',         sub: 'party_type=couple · gender=male_female' },
                { key: 'couple_ff',   label: 'COUPLE (F+F)',         sub: 'party_type=couple · gender=female_female' },
              ];
              const current = Array.isArray(formData.waitlist_categories) ? formData.waitlist_categories : ['stag_male'];
              const toggle = (key) => {
                const next = current.includes(key)
                  ? current.filter((k) => k !== key)
                  : [...current, key];
                setFormData({ ...formData, waitlist_categories: next });
              };
              const allKeys = CATS.map((c) => c.key);
              const isAll = allKeys.every((k) => current.includes(k));
              const toggleAll = () => {
                setFormData({ ...formData, waitlist_categories: isAll ? [] : allKeys });
              };
              return (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {CATS.map((c) => {
                      const checked = current.includes(c.key);
                      return (
                        <label
                          key={c.key}
                          className={`flex items-start gap-3 p-3 border cursor-pointer select-none ${
                            checked
                              ? 'border-amber-500/50 bg-amber-500/5'
                              : 'border-zinc-800 bg-[#050505] hover:border-zinc-700'
                          }`}
                          data-testid={`waitlist-cat-label-${c.key}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(c.key)}
                            className="accent-amber-400 w-4 h-4 mt-0.5"
                            data-testid={`waitlist-cat-${c.key}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-xs text-white tracking-wider">{c.label}</div>
                            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{c.sub}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="font-mono text-[11px] text-amber-400 hover:underline tracking-wider"
                    data-testid="waitlist-all-toggle"
                  >
                    {isAll ? 'DESELECT ALL · Everyone auto-confirms' : 'SELECT ALL · Everyone needs manual approval'}
                  </button>
                </>
              );
            })()}
          </div>

          {/* Discounts — only for ticketed events; needs event to be saved first */}
          {formData.event_type !== 'guest_list' && (
            <DiscountsSection eventId={editingEvent?.id || null} />
          )}

          {/* Submit */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary flex-1"
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="save-event-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  SAVING...
                </>
              ) : (
                editingEvent ? 'UPDATE EVENT' : 'CREATE EVENT'
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventFormDialog;
